import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {CLIENT_LOGO, VERSION_APP} from '../../helpers/consts'
import {RESET_PASSWORD} from '../../gql/mutations/authMutations'

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'Minimum 6 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Password confirmation is required'),
})

export const ResetPasswordPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [resetPassword, {loading}] = useMutation(RESET_PASSWORD)
  const [resetDone, setResetDone] = useState(false)

  const params = new URLSearchParams(location.search)
  const uid = params.get('uid') || ''
  const token = params.get('token') || ''
  const isInvitation = params.get('invite') === '1' || params.get('invite') === 'true'
  const hasToken = Boolean(uid && token)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!hasToken) {
        toast.error('Invalid or expired reset link.')
        return
      }
      try {
        const response = await resetPassword({
          variables: {
            uid,
            token,
            newPassword: values.password,
          },
        })
        const ok = response.data?.resetPassword?.ok
        const message = response.data?.resetPassword?.message || ''
        if (ok) {
          toast.success(message || 'Password updated.')
          setResetDone(true)
          setTimeout(() => navigate('/login'), 1200)
        } else {
          toast.error(message || 'Invalid or expired reset link.')
        }
      } catch (error) {
        toast.error('An error occurred. Please try again.')
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
              <div className='text-center mb-11'>
                <img src='/media/tfi/logoNColorP.png' width={200} alt='logo' />
                <h1 className='text-dark fw-bolder mb-3'>
                  {isInvitation ? 'Set Up Your Account' : 'Reset Password'}
                </h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                  {isInvitation
                    ? 'Create a password to activate your account.'
                    : 'Choose a new password for your account.'}
                </div>
              </div>

              {!hasToken && (
                <div className='mb-10 alert alert-danger'>
                  Invalid or expired reset link.
                </div>
              )}
              {resetDone && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>Password updated. Redirecting to login.</div>
                </div>
              )}

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>New password</label>
                <input
                  placeholder='New password'
                  {...formik.getFieldProps('password')}
                  className={clsx(
                    'form-control bg-transparent',
                    {'is-invalid': formik.touched.password && formik.errors.password},
                    {
                      'is-valid': formik.touched.password && !formik.errors.password,
                    }
                  )}
                  type='password'
                  name='password'
                />
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.password}
                    </span>
                  </div>
                )}
              </div>

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>Confirm password</label>
                <input
                  placeholder='Confirm password'
                  {...formik.getFieldProps('confirmPassword')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'is-invalid':
                        formik.touched.confirmPassword && formik.errors.confirmPassword,
                    },
                    {
                      'is-valid':
                        formik.touched.confirmPassword && !formik.errors.confirmPassword,
                    }
                  )}
                  type='password'
                  name='confirmPassword'
                />
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.confirmPassword}
                    </span>
                  </div>
                )}
              </div>

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  className='btn btn-primary mt-4'
                  disabled={!hasToken || formik.isSubmitting || !formik.isValid || loading}
                >
                  {!loading && (
                    <span className='indicator-label'>
                      {isInvitation ? 'Set password' : 'Update password'}
                    </span>
                  )}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6'>
                <Link to='/login' className='link-primary'>
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5'>
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt='Logo' src={toAbsoluteUrl(CLIENT_LOGO)} className='h-75px' />
          </span>
          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />
          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            Fast, Efficient and Productive
          </h1>
        </div>
      </div>
    </div>
  )
}
